import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Loader2, AlertCircle } from "lucide-react";
import HelpContentView from "./HelpContentView";
import { fetchHelpContent } from "./helpApi";

/**
 * Sağdan kayan yardım paneli.
 *
 * Neden modal DEĞİL de yan panel: kullanıcı talimatı okurken sayfayı görmeye
 * devam etmeli ("sağ üstteki yeşil butona basın" derken buton ekranda
 * durmalı). Modal ekranı kapatır, ayrı sayfa ise kullanıcının yaptığı işi
 * kaybettirir.
 *
 * Radix Sheet yerine düz div: bu proje jest/jsdom altında Radix portal'ları
 * ile sorun yaşıyor; panel basit olduğu için bağımlılığa gerek yok.
 */
const HelpDrawer = ({ open, pageId, onClose, onNavigate }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const closeButtonRef = useRef(null);
  // Kapatma yalnız arka planda BAŞLAYAN tıklamada olur. Panel içinden metin
  // seçip fareyi dışarı sürükleyince kapanmasın diye.
  const pressStartedOnBackdrop = useRef(false);

  useEffect(() => {
    if (!open || !pageId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchHelpContent(pageId)
      .then((data) => {
        if (!cancelled) setDoc(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setDoc(null);
        setError(
          err?.response?.status === 404
            ? "Bu sayfa için yardım metni henüz hazırlanmadı."
            : "Yardım metni yüklenemedi. Lütfen tekrar deneyin."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, pageId]);

  // ESC ile kapat
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open && closeButtonRef.current) closeButtonRef.current.focus();
  }, [open]);

  const handleNavigate = useCallback(
    (targetPageId) => {
      if (onNavigate) {
        onNavigate(targetPageId);
        onClose();
      }
    },
    [onNavigate, onClose]
  );

  if (!open) return null;

  return (
    <>
      {/* Arka plan — tıklayınca kapanır ama sayfayı KARARTMAZ (yarı saydam),
          çünkü kullanıcının anlatılan ekranı görmeye devam etmesi gerekiyor.

          DİKKAT — `inset-0` KULLANMA. index.css'te
              .fixed.inset-0 { z-index: 9999 !important; }
          global kuralı var (Radix dialog overlay'leri için). O sınıf ikilisi
          buraya da uygulanırsa arka plan panelin ÜSTÜNE çıkar; panel
          tıklanamaz/kaydırılamaz hâle gelir ve metin seçilemez. Bu yüzden
          kenarlar tek tek veriliyor ve z-index açıkça yazılıyor. */}
      <div
        className="fixed top-0 right-0 bottom-0 left-0 bg-black/10 z-[9990]"
        onMouseDown={(e) => {
          pressStartedOnBackdrop.current = e.target === e.currentTarget;
        }}
        onClick={(e) => {
          if (pressStartedOnBackdrop.current && e.target === e.currentTarget) {
            onClose();
          }
          pressStartedOnBackdrop.current = false;
        }}
        data-testid="help-backdrop"
        aria-hidden="true"
      />

      {/* z-index arka planın bir üstünde ama Radix dialog bandının (9998/9999)
          ALTINDA — panel açıkken bir dialog açılırsa dialog üstte kalmalı. */}
      <aside
        role="dialog"
        aria-modal="false"
        aria-label="Sayfa yardımı"
        data-testid="help-drawer"
        className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-gray-50 shadow-2xl z-[9991] flex flex-col border-l-4 border-green-500"
      >
        <header className="flex items-start justify-between gap-3 px-4 py-3 bg-white border-b">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-green-700 font-semibold">
              Kullanım Talimatı
            </p>
            <h2 className="text-lg font-bold text-gray-900 truncate">
              {doc?.title || "Yardım"}
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Yardımı kapat"
            className="flex-shrink-0 p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* `min-h-0` şart: flex kolonda çocuğun varsayılan `min-height:auto`
            değeri küçülmeyi engeller, overflow-y-auto hiç devreye girmez.
            `overscroll-contain` panel sonuna gelince arkadaki sayfanın
            kaymasını önler. Metin seçilebilir kalmalı (kopyalama). */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 select-text">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Yükleniyor...
            </div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-2 text-sm text-gray-700 bg-white border rounded-lg p-4">
              <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && (
            <HelpContentView doc={doc} onNavigate={onNavigate ? handleNavigate : undefined} />
          )}
        </div>

        <footer className="px-4 py-2 bg-white border-t text-[11px] text-gray-500">
          Kapatmak için <kbd className="px-1 border rounded">Esc</kbd> · Açmak için{" "}
          <kbd className="px-1 border rounded">F1</kbd>
        </footer>
      </aside>
    </>
  );
};

export default HelpDrawer;
